import { createFileRoute, Link } from "@tanstack/react-router";
import { pageMeta, ldScripts, breadcrumbLd, faqLd } from "@/lib/seo";
import { siteConfig } from "@/config/site";

import { useMemo, useState } from "react";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { BLOG_CATEGORIES, listBlogPosts, type BlogCategory } from "@/content/blog";
import { Picture } from "@/components/media/Picture";

const BLOG_FAQS = [
  {
    q: `How often does ${siteConfig.name} publish new field notes?`,
    a: "We publish when an engagement produces something worth reporting, not on a fixed calendar. Most months bring two to four new posts; a quiet month means nothing changed worth writing up, not that we've gone dark.",

  },
  {
    q: "Are these posts written by the people doing the work?",
    a: "Yes. Every article is written or reviewed by the strategist or engineer who ran the engagement it describes. We don't outsource field notes to generalist copywriters, because the value is in the specificity.",
  },
  {
    q: "Do you update old posts when the guidance changes?",
    a: "We do. Search evolves quickly enough — especially around AI Overviews and GEO — that guidance from twelve months ago can mislead. Updated posts carry a revised date; we don't silently rewrite history without a note.",
  },
  {
    q: "What's the difference between the blog and the guides section?",
    a: "The blog is narrow and timely: one experiment, one finding, one client situation. The guides are broad and durable: full operating manuals meant to be referenced repeatedly across a project's lifetime.",
  },
  {
    q: "Can I use these posts to do the work myself?",
    a: "Many readers do exactly that, and we write with that in mind. We're not withholding the mechanics to force a sales conversation — if you'd rather have us execute it, that's a separate decision.",
  },
  {
    q: "Do you cover failures as well as wins?",
    a: "Deliberately, yes. A tactic that underperformed on a client site is often more useful to a reader than another success story, because it tells you where the edge cases are.",
  },
  {
    q: "How do you decide what counts as a category on the blog?",
    a: "Categories map to the disciplines we actually staff for — AI search, technical SEO, local SEO, content, and case teardowns. We don't create a category until we have a sustained body of real work behind it.",
  },
];

export const Route = createFileRoute("/blog/")({
  head: () => ({ ...pageMeta({
    path: "/blog/",
    title: `SEO & AI Search Blog — Field Notes | ${siteConfig.name}`,

    description:
      `Editorial field notes from live SEO and AI-search engagements from ${siteConfig.name}: AI Overviews, GEO, technical SEO, local SEO, Core Web Vitals, and case teardowns.`,

    keywords: [
      "SEO blog",
      "AI search blog",
      "AEO blog",
      "GEO blog",
      "AI Overviews blog",
      "local SEO field notes",
      "technical SEO blog",
      "SEO case teardowns",
    ],
  }), scripts: ldScripts(
    breadcrumbLd([{ name: "Home", path: "/" }, { name: "Blog", path: "/blog/" }]),
    faqLd(BLOG_FAQS),
  ) }),
  component: BlogIndex,
});

function BlogIndex() {
  const [cat, setCat] = useState<BlogCategory | "All">("All");
  const posts = useMemo(() => {
    const all = listBlogPosts().sort((a, b) => 
      new Date(b.publishedISO).getTime() - new Date(a.publishedISO).getTime()
    );
    return cat === "All" ? all : all.filter((p) => p.category === cat);
  }, [cat]);

  const [featured, ...rest] = posts;

  return (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1}>
        <section className="container-page pt-24 pb-10">
          <EyebrowLabel>The publication</EyebrowLabel>
          <h1 className="mt-4 font-display text-5xl md:text-7xl leading-[0.95] tracking-tight">
            Field notes.
            <br />
            <span className="text-primary">Not opinions.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground leading-relaxed">
            Everything on this page is written from inside live engagements — what we watched
            change, what we shipped, what broke, and what compounded. If it isn't grounded in
            work, it isn't published.
          </p>
        </section>

        <section className="container-page pb-6">
          <div className="flex flex-wrap gap-2">
            {(["All", ...BLOG_CATEGORIES] as const).map((c) => (
              <button
                key={c}
                onClick={() => setCat(c as BlogCategory | "All")}
                className={`rounded-full px-4 py-1.5 text-xs font-medium tracking-wide transition-colors border ${
                  cat === c
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </section>

        {featured && (
          <section className="container-page pb-14">
            <a
              href={`/${featured.slug}/`}
              className="group block rounded-3xl border border-border overflow-hidden bg-card/40 hover:border-primary/40 transition-all"
            >
              <div className="grid md:grid-cols-[1.4fr_1fr]">
                <div className="p-8 md:p-12">
                  <EyebrowLabel>Featured · {featured.category}</EyebrowLabel>
                  <h2 className="mt-4 font-display text-3xl md:text-5xl tracking-tight leading-[1.05]">
                    {featured.title}
                  </h2>
                  <p className="mt-5 text-lg text-muted-foreground leading-relaxed max-w-2xl">
                    {featured.dek}
                  </p>
                  <div className="mt-8 flex items-center gap-4 text-xs text-muted-foreground uppercase tracking-widest">
                    <span>{new Date(featured.publishedISO).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                    <span>·</span>
                    <span>{featured.readingMinutes} min read</span>
                  </div>
                </div>
                <div className="hidden md:block relative overflow-hidden bg-surface-2">
                  <Picture
                    variantKey="blog-default"
                    alt={`Featured ${siteConfig.name} article: ${featured.title}`}
                    className="absolute inset-0 h-full w-full block"
                    imgClassName="absolute inset-0 h-full w-full object-cover opacity-70 group-hover:opacity-90 transition-opacity"
                    sizes="(min-width: 768px) 50vw, 100vw"
                  />
                  <div className="relative p-12 h-full flex items-end bg-gradient-to-br from-background/80 via-background/40 to-transparent">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-muted-foreground">Key takeaways</div>
                      <ul className="mt-3 space-y-2 text-sm text-foreground/95">
                        {featured.keyTakeaways.slice(0, 3).map((k) => (
                          <li key={k} className="flex gap-2"><span className="text-primary">·</span>{k}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </a>
          </section>
        )}

        <section className="container-page pb-32 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((p) => (
            <a
              key={p.slug}
              href={`/${p.slug}/`}
              className="group flex flex-col rounded-2xl border border-border p-7 bg-card/30 hover:bg-card/50 hover:border-primary/40 transition-all"
            >
              <EyebrowLabel>{p.category}</EyebrowLabel>
              <h3 className="mt-3 font-display text-2xl leading-tight tracking-tight">
                {p.title}
              </h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-3">{p.dek}</p>
              <div className="mt-auto pt-6 flex items-center gap-3 text-xs text-muted-foreground uppercase tracking-widest">
                <span>{new Date(p.publishedISO).toLocaleDateString("en-US", { month: "short", year: "numeric" })}</span>
                <span>·</span>
                <span>{p.readingMinutes} min</span>
              </div>
            </a>
          ))}
        </section>

        <section className="container-page pb-20 border-t border-border pt-16">
          <div className="grid gap-12 lg:grid-cols-[1.1fr_1fr]">
            <div>
              <EyebrowLabel>What this collection covers</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
                A running log of live engagements, not a content calendar.
              </h2>
              <div className="mt-6 space-y-4 text-foreground/85 leading-relaxed">
                <p>
                  This blog exists because most SEO commentary is written at a distance from
                  the work — screenshots of algorithm updates, secondhand summaries of
                  someone else's case study, predictions dressed up as findings. Ours isn't.
                  Every post here traces back to a client account, an internal audit, or a
                  test we ran on our own properties before recommending it to anyone else.
                </p>
                <p>
                  It's written for three overlapping audiences: in-house marketers who need a
                  second opinion before committing budget, agency operators who want to see
                  how a competitor thinks about a problem, and business owners who want proof
                  that the person doing their SEO actually understands the mechanics rather
                  than repeating vendor talking points.
                </p>
                <p>
                  The categories on this page — AI search, technical SEO, local SEO, content,
                  and case teardowns — aren't arbitrary tags. They map directly to the
                  disciplines we staff for on client accounts, so a category filter here is
                  effectively a filter on "what kind of work generated this observation".
                </p>
              </div>
            </div>
            <div>
              <EyebrowLabel>Editorial standards</EyebrowLabel>
              <h2 className="mt-4 font-display text-2xl md:text-3xl tracking-tight leading-tight">
                How we research and publish
              </h2>
              <ul className="mt-6 space-y-4">
                {[
                  ["Sourced from real accounts", "Every claim is checked against Search Console, GA4, log files, or a rank-tracking export before it's written down. If we can't point to the data, we don't publish the claim."],
                  ["Named authorship", "Posts carry the byline of the practitioner who did the work, not a house pseudonym. You can trace a post back to a person and a track record."],
                  ["No manufactured statistics", "We don't invent percentages to make a headline punchier. If a number appears, it came from a dashboard, not a guess."],
                  ["Dated and revisited", "Every post is timestamped, and posts affected by a confirmed algorithm or SERP-feature change get revised with a visible update date rather than being quietly rewritten."],
                  ["Competing views included", "Where the industry genuinely disagrees — attribution models, canonical crawl-budget theory, or how much AI Overviews cannibalise clicks — we say so instead of pretending consensus exists."],
                ].map(([h, b]) => (
                  <li key={h} className="rounded-xl border border-border/70 bg-background/40 p-5">
                    <div className="font-display text-base tracking-tight">{h}</div>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{b}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="container-page pb-20">
          <EyebrowLabel>Reading order</EyebrowLabel>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
            If you're new here, read in this order
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground leading-relaxed">
            The blog isn't sequential the way a guide is, but readers new to ${siteConfig.name}

            tend to get the most value by moving from context to mechanics to proof:
          </p>
          <ol className="mt-6 grid gap-4 md:grid-cols-3">
            <li className="rounded-xl border border-border p-5">
              <div className="text-xs uppercase tracking-widest text-primary">Step 1 — Orientation</div>
              <p className="mt-2 text-sm text-foreground/85 leading-relaxed">
                Start with the AI search category to understand how AI Overviews and generative
                answers are changing what "ranking" means before you dig into tactics.
              </p>
            </li>
            <li className="rounded-xl border border-border p-5">
              <div className="text-xs uppercase tracking-widest text-primary">Step 2 — Mechanics</div>
              <p className="mt-2 text-sm text-foreground/85 leading-relaxed">
                Move to technical SEO and local SEO posts, which tend to be the most actionable
                and the easiest to apply directly to your own site this week.
              </p>
            </li>
            <li className="rounded-xl border border-border p-5">
              <div className="text-xs uppercase tracking-widest text-primary">Step 3 — Proof</div>
              <p className="mt-2 text-sm text-foreground/85 leading-relaxed">
                Finish with the case teardowns, which show the mechanics from steps one and two
                applied together on a real account, warts included.
              </p>
            </li>
          </ol>
        </section>

        <section className="container-page pb-20">
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <EyebrowLabel>How this connects</EyebrowLabel>
              <h2 className="mt-4 font-display text-2xl md:text-3xl tracking-tight leading-tight">
                Blog posts, guides, and services aren't separate silos
              </h2>
              <p className="mt-4 text-foreground/85 leading-relaxed">
                A blog post is usually a single thread pulled out of a broader pillar guide or a
                live service engagement. When a post references a technique in depth, it links
                through to the relevant <Link to={"/guides/" as any}   className="text-primary hover:underline">pillar guide</Link> for
                the full operating manual, and to the <Link to={"/services/" as any}   className="text-primary hover:underline">service page</Link> where
                that work is actually delivered for clients. The <Link to={"/glossary/" as any}   className="text-primary hover:underline">glossary</Link> handles
                the vocabulary layer underneath both — if a post uses a term you don't
                recognise, it's almost certainly defined there with an example.
              </p>
              <p className="mt-4 text-foreground/85 leading-relaxed">
                Read together, the three sections form a stack: glossary for definitions, guides
                for the full method, blog for how the method behaves in the field.
              </p>
            </div>
            <div>
              <EyebrowLabel>Common misconceptions</EyebrowLabel>
              <h2 className="mt-4 font-display text-2xl md:text-3xl tracking-tight leading-tight">
                What readers get wrong about a blog like this
              </h2>
              <ul className="mt-6 space-y-3">
                {[
                  "“Frequent publishing means better SEO for the agency itself.” Cadence is a byproduct of client work, not a growth-hacking schedule — we won't publish filler just to hit a number.",
                  "“Older posts are automatically outdated.” Foundational mechanics — crawling, indexation, internal linking — change slowly; only the SERP-feature-specific posts need frequent revision.",
                  "“A case teardown proves the tactic will work for everyone.” A single account's result is evidence, not a guarantee; we flag context (industry, starting authority, budget) precisely so you can judge fit.",
                  "“AI-search posts are hype.” AI Overviews already appear on a meaningful share of commercial queries in our client data; ignoring the category isn't a neutral choice, it's a bet.",
                ].map((t) => (
                  <li key={t} className="flex gap-3 text-sm text-foreground/85 leading-relaxed">
                    <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="container-page pb-32">
          <EyebrowLabel>FAQ</EyebrowLabel>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
            Questions readers ask about the blog
          </h2>
          <dl className="mt-8 grid gap-6 md:grid-cols-2">
            {BLOG_FAQS.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border p-6">
                <dt className="font-display text-lg tracking-tight">{f.q}</dt>
                <dd className="mt-2 text-sm text-foreground/85 leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>

      </main>
      <SiteFooter />
    </div>
  );
}
